import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@cloud-ui/contexts/AuthContext';
import { useToast } from '@cloud-ui/contexts/ToastContext';
import { getApiErrorMessage } from '@cloud-ui/utils/api';
import {
  addTeamMember,
  createTeam,
  createTeamSecret,
  deleteTeam,
  deleteTeamSecret,
  fetchTeams,
  getCurrentUserTeams,
  getTeam,
  getTeamInferenceProviders,
  getTeamMembers,
  getTeamRedteamProvider,
  getTeamSecrets,
  getTeamSecretValue,
  getTeamUsage,
  removeTeamMember,
  updateTeam,
  updateTeamInferenceProvider,
  updateTeamMemberRole,
  updateTeamRedteamProvider,
  updateTeamSecret,
} from '@cloud-ui/utils/api/teams';
import { ServerSettingsKeys } from '@shared/dto/serverSettings';
import type { TeamInferenceProviderKey, TeamProbeUsageDTO } from '@shared/dto/teams';

export const useTeams = () => {
  const { organization } = useAuth();
  return useQuery({
    queryKey: ['teams', organization?.id],
    queryFn: fetchTeams,
    enabled: !!organization,
  });
};

export const useTeam = (teamId: string) => {
  return useQuery({
    queryKey: ['teams', teamId],
    queryFn: () => getTeam(teamId),
    enabled: !!teamId,
    staleTime: 5000, // Cache for 5 seconds
  });
};

export const useTeamMembers = (teamId: string) => {
  return useQuery({
    queryKey: ['teams', teamId, 'members'],
    queryFn: () => getTeamMembers(teamId),
    enabled: !!teamId,
    staleTime: 5000, // Cache for 5 seconds
  });
};

export const useTeamUsage = (teamId: string | null | undefined) => {
  return useQuery<TeamProbeUsageDTO>({
    queryKey: ['teams', teamId, 'usage'],
    queryFn: () => getTeamUsage(teamId as string),
    enabled: !!teamId,
    staleTime: 30 * 1000,
  });
};

export const useTeamRedteamProvider = (teamId: string | null | undefined) => {
  return useQuery({
    queryKey: ['teams', teamId, 'redteam-provider'],
    queryFn: () => getTeamRedteamProvider(teamId as string),
    enabled: !!teamId,
    staleTime: 5000,
  });
};

export const useTeamInferenceProviders = (teamId: string | null | undefined) => {
  return useQuery({
    queryKey: ['teams', teamId, 'inference-providers'],
    queryFn: () => getTeamInferenceProviders(teamId as string),
    enabled: !!teamId,
    staleTime: 5000,
  });
};

export const useCreateTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['currentUserTeams'] });
    },
  });
};

export const useUpdateTeam = (teamId: string) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (changes: Parameters<typeof updateTeam>[1]) => updateTeam(teamId, changes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['currentUserTeams'] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'service-accounts'] });
      showToast('Team updated successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to update team:', error);
      showToast('Failed to update team', 'error');
    },
  });
};

export const useUpdateTeamRedteamProvider = (teamId: string) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: Parameters<typeof updateTeamRedteamProvider>[1]) =>
      updateTeamRedteamProvider(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId, 'redteam-provider'] });
      queryClient.invalidateQueries({ queryKey: ['teams', teamId] });
      showToast('Red Team Provider updated successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to update team Red Team Provider:', error);
      showToast(getApiErrorMessage(error, 'Failed to update Red Team Provider'), 'error');
    },
  });
};

export const useUpdateTeamInferenceProvider = (teamId: string, key: TeamInferenceProviderKey) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: Parameters<typeof updateTeamInferenceProvider>[2]) =>
      updateTeamInferenceProvider(teamId, key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId, 'inference-providers'] });
      if (key === ServerSettingsKeys.redteamProvider) {
        queryClient.invalidateQueries({ queryKey: ['teams', teamId, 'redteam-provider'] });
      }
      showToast('Inference Provider updated successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to update team Inference Provider:', error);
      showToast(getApiErrorMessage(error, 'Failed to update Inference Provider'), 'error');
    },
  });
};

export const useDeleteTeam = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: deleteTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['currentUserTeams'] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'service-accounts'] });
      showToast('Team deleted successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to delete team:', error);
      showToast('Failed to delete team', 'error');
    },
  });
};

export const useAddTeamMember = (teamId: string) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => addTeamMember(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['currentUserTeams'] });
      showToast('Team member added successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to add team member:', error);
      showToast('Failed to add team member', 'error');
    },
  });
};

export const useRemoveTeamMember = (teamId: string) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (userId: string) => removeTeamMember(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['currentUserTeams'] });
      showToast('Team member removed successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to remove team member:', error);
      showToast('Failed to remove team member', 'error');
    },
  });
};

export const useUpdateTeamMemberRole = (teamId: string) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      updateTeamMemberRole(teamId, userId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['currentUserTeams'] });
      showToast('Role updated successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to update role:', error);
      showToast('Failed to update role', 'error');
    },
  });
};

export const useCurrentUserTeams = () => {
  const { user, organization } = useAuth();
  return useQuery({
    queryKey: ['currentUserTeams', user?.id, organization?.id],
    queryFn: () => getCurrentUserTeams(),
    enabled: !!user && !!organization,
    staleTime: 30 * 1000,
  });
};

export const useTeamSecrets = (teamId: string | null | undefined) => {
  return useQuery({
    queryKey: ['teams', teamId, 'secrets'],
    queryFn: () => getTeamSecrets(teamId as string),
    enabled: !!teamId,
    staleTime: 5000,
  });
};

export const useTeamSecretValue = (
  teamId: string | null | undefined,
  secretId: string | null | undefined,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ['teams', teamId, 'secrets', secretId, 'value'],
    queryFn: () => getTeamSecretValue(teamId as string, secretId as string),
    enabled: !!teamId && !!secretId && enabled,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useCreateTeamSecret = (teamId: string) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: Parameters<typeof createTeamSecret>[1]) => createTeamSecret(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId, 'secrets'] });
      showToast('Team secret created successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to create team secret:', error);
      showToast(getApiErrorMessage(error, 'Failed to create team secret'), 'error');
    },
  });
};

export const useUpdateTeamSecret = (teamId: string) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({
      secretId,
      data,
    }: {
      secretId: string;
      data: Parameters<typeof updateTeamSecret>[2];
    }) => updateTeamSecret(teamId, secretId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId, 'secrets'] });
      showToast('Team secret updated successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to update team secret:', error);
      showToast(getApiErrorMessage(error, 'Failed to update team secret'), 'error');
    },
  });
};

export const useDeleteTeamSecret = (teamId: string) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (secretId: string) => deleteTeamSecret(teamId, secretId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', teamId, 'secrets'] });
      showToast('Team secret deleted successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to delete team secret:', error);
      showToast(getApiErrorMessage(error, 'Failed to delete team secret'), 'error');
    },
  });
};
